import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import StudentFormDialog from '../../components/admin/StudentFormDialog';
import {
  ConfirmDialog,
  DataTable,
  LoadingState,
  PageHeader,
  Toast,
} from '../../components/admin/ui';

const StudentsManagement = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const fetchStudents = async () => {
    try {
      const response = await api.get('/admin/students');
      setStudents(response.data.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const photoUrl = (path) => {
    if (!path) return null;
    const base = (process.env.REACT_APP_API_URL || '').replace('/api', '');
    return `${base}/uploads/${path}`;
  };

  const handleDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/students/${confirmId}`);
      setConfirmId(null);
      setToast({ open: true, message: 'Alumno eliminado', tone: 'success' });
      fetchStudents();
    } catch (error) {
      console.error('Error deleting student:', error);
      setToast({ open: true, message: 'Error al eliminar el alumno', tone: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <LoadingState />;

  const columns = [
    {
      key: 'avatar',
      header: 'Avatar',
      render: (row) => {
        const src = photoUrl(row.photo_path);
        return src ? (
          <img src={src} alt="" className="h-10 w-10 rounded-full object-cover" />
        ) : (
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
            {(row.first_name?.[0] || '') + (row.last_name?.[0] || '')}
          </span>
        );
      },
    },
    {
      key: 'name',
      header: 'Nombre',
      render: (row) => `${row.first_name} ${row.last_name}`,
    },
    {
      key: 'phone',
      header: 'Teléfono',
      render: (row) => row.phone || '—',
    },
    {
      key: 'belt',
      header: 'Cinturón',
      render: (row) =>
        row.belt_name ? (
          <span
            className="rounded-full px-2 py-0.5 text-xs font-semibold text-white"
            style={{ backgroundColor: row.belt_color || '#000' }}
          >
            {row.belt_name}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      header: 'Acciones',
      className: 'text-right',
      render: (row) => (
        <div className="flex justify-end gap-1">
          <button
            type="button"
            className="btn-admin-ghost !min-h-9"
            onClick={() => {
              setSelectedStudent(row);
              setDialogOpen(true);
            }}
          >
            Editar
          </button>
          <button
            type="button"
            className="btn-admin-ghost !min-h-9 text-danger"
            onClick={() => setConfirmId(row.id)}
          >
            Eliminar
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <PageHeader
        title="Alumnos"
        onBack={() => navigate('/admin')}
        action={
          <button
            type="button"
            className="btn-admin"
            onClick={() => {
              setSelectedStudent(null);
              setDialogOpen(true);
            }}
          >
            Agregar
          </button>
        }
      />

      <DataTable columns={columns} rows={students} emptyTitle="No hay alumnos registrados" />

      <StudentFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
        onSuccess={fetchStudents}
      />

      <ConfirmDialog
        open={Boolean(confirmId)}
        title="Eliminar alumno"
        message="¿Estás seguro de eliminar este alumno?"
        confirmLabel="Eliminar"
        danger
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setConfirmId(null)}
      />

      <Toast
        open={toast.open}
        message={toast.message}
        tone={toast.tone}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </div>
  );
};

export default StudentsManagement;
